import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useDbHealth } from "@/ui/data/useDbHealth";
import { useMythicDevSurfaces } from "@/lib/mythic/featureFlags";

export default function AppShell() {
  const location = useLocation();
  const { user, isProfileCreating } = useAuth();
  const devSurfaces = useMythicDevSurfaces();
  const isLoginRoute = location.pathname === "/login" || location.pathname === "/signup";
  const isMythicRoute = location.pathname.startsWith("/mythic/");
  const shouldPollDb = devSurfaces.enabled && Boolean(user) && !isLoginRoute;
  const { status, lastError, lastProbe } = useDbHealth(shouldPollDb);
  const navLinks = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/dashboard#create", label: "Create/Join" },
    { to: "/dashboard#campaigns", label: "Mythic" },
    { to: "/servers", label: "Servers/Admin" },
    ...(import.meta.env.DEV ? [{ to: "/dev/narrator-test", label: "Narrator Test" }] : []),
  ];

  if (isMythicRoute) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground atmosphere-night">
      <div className="mx-auto flex min-h-screen w-full max-w-[1720px] gap-3 px-2 py-2 sm:px-3 sm:py-3">
        <aside className="mythic-shell hidden w-64 shrink-0 p-4 lg:block">
          <div className="mb-6">
            <div className="cinematic-title text-lg font-semibold text-amber-100">Saga Spark</div>
            <div className="cinematic-subtitle text-xs">Obsidian Operations Console</div>
          </div>
          <nav className="flex flex-col gap-2 text-sm">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `cinematic-nav-link px-2.5 py-1.5 ${isActive ? "cinematic-nav-link-active text-amber-50" : "text-muted-foreground"}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="mythic-shell flex min-h-screen min-w-0 flex-1 flex-col">
          <div className="border-b border-border/70 bg-card/55 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="cinematic-title text-base font-semibold text-amber-100 sm:text-lg">Operations Stage</div>
                <div className="text-xs text-muted-foreground">Route: {location.pathname}</div>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>{user?.email ?? "guest"}</span>
                <span>DB: {shouldPollDb ? (status === "ok" ? "ok" : status) : "paused"}</span>
                {isProfileCreating ? <span>profile creating...</span> : null}
              </div>
            </div>
            <details className="mt-2 rounded border border-border/60 bg-background/30 p-2 text-xs lg:hidden">
              <summary className="cursor-pointer list-none text-amber-100/85">World status</summary>
              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                <div>Auth: {user ? "ready" : "guest"}</div>
                <div>DB status: {shouldPollDb ? status : "paused"}</div>
                {lastProbe ? <div>DB probe: {lastProbe.elapsedMs}ms</div> : null}
                {lastProbe ? <div>Probe result: {lastProbe.timedOut ? "timeout" : lastProbe.status ?? "n/a"}</div> : null}
              </div>
            </details>
          </div>

          {devSurfaces.enabled && lastError ? (
            <div className="border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-xs text-destructive">
              DB Error: {lastError}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto px-4 pb-20 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pb-8">
            <Outlet />
          </div>

          <nav className="fixed inset-x-2 bottom-2 z-50 rounded-xl border border-amber-200/25 bg-[linear-gradient(180deg,rgba(25,18,11,0.95),rgba(8,10,16,0.98))] p-1 shadow-xl lg:hidden">
            <div className="grid grid-cols-4 gap-1">
              {navLinks.slice(0, 4).map(link => (
                <NavLink
                  key={`mobile-${link.to}`}
                  to={link.to}
                  className={({ isActive }) =>
                    `rounded-md px-2 py-2 text-center text-[11px] ${isActive ? "bg-amber-300/20 text-amber-50" : "text-amber-100/75"}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </nav>
        </main>
      </div>
    </div>
  );
}
